import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { FormEvent, forwardRef, useMemo, useRef, useState } from 'react'

import { ArrowRight } from 'almanac-ui/icons/16'
import { Spinner } from 'common/Spinner/Spinner'
import { Button } from 'blaze-ui/Button'
import { REACT_APP_FRONTEND_URL } from 'env'
import { PAYMENT_STATUS_PATH } from 'paths'
import { TRIAL_REQUESTED, trackEvent } from 'utils/trackingUtils'
import { useField as useInputField } from '@react-aria/label'
import Styles from './FreeTrialCardForm.module.scss'
import cx from 'classnames'
import { Input } from 'almanac-ui/TextInputField/Input'
import { useObjectRef } from '@react-aria/utils'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import { reportError } from 'utils/error'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Testimonials } from '../../../Testimonials/Testimonials'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { PromotionCode, StripePromotionCodeClient } from 'api/clients/StripePromotionCodeClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { MonthlyAnnualToggle, PlanInterval } from '../MonthlyAnnualToggle/MonthlyAnnualToggle'
import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'

import { useQueryParams } from 'hooks/useQueryParams'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { X02 } from 'blaze-ui/icons/16'
import { Text } from 'blaze-ui/Text/Text'

const ANNUAL_DISCOUNT_PERCENT = 33

interface Props {
  isProcessing: boolean
  isPaywall: boolean
  setIsProcessing: (isProcessing: boolean) => void
  redirectTo?: string
  onboardingDocPath?: string
  registrationType?: string
  promotionCode?: string
  setAppliedPromoCode: (promoCode?: PromotionCode) => void
  readyOverride?: boolean
  paymentPlans: PaymentPlan[]
  infoPanel: React.ReactNode
  newlyCreatedWorkspaceId: number
}

interface CardholderNameInputProps {
  isDisabled?: boolean
  isInvalid?: boolean
  setIsInvalid?: (isInvalid: boolean) => void
}

export const CardholderNameInput = forwardRef(
  ({ isDisabled, isInvalid, setIsInvalid, ...props }: CardholderNameInputProps, fRef: any) => {
    const isSystemRedesignEnabled = useIsSystemRedesign()
    const [value, setValue] = useState('')
    const { labelProps, fieldProps } = useInputField({ ...props, 'aria-label': 'Name on card' })
    const inputRef = useObjectRef<HTMLInputElement>(fRef)

    return (
      <div className={cx(Styles.cardHolderInput, { [Styles.invalid]: isInvalid })}>
        <label htmlFor="cardholderName" {...labelProps}>
          Name on card
        </label>

        <Input
          name="cardholder-name"
          data-testid="cardholder-name-input"
          id="cardholderName"
          autoFocus
          size={isSystemRedesignEnabled ? 'xl' : 'md'}
          placeholder="Cardholder's Name"
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            if (isInvalid) setIsInvalid?.(false)
          }}
          aria-disabled={isDisabled}
          disabled={isDisabled}
          {...fieldProps}
          ref={inputRef}
        />

        <div className={Styles.error}>Your cardholder's name is incomplete</div>
      </div>
    )
  },
)

interface PromotionCodeInputProps {
  isDisabled?: boolean
  isInvalid?: boolean
  isApplied?: boolean
  isApplying?: boolean
  appliedCodeName?: string
  appliedDescription?: string
  onBlur: () => void
  onApply?: () => void
  onClear?: () => void
}

export const PromotionCodeInput = forwardRef(({ ...props }: PromotionCodeInputProps, fRef: any) => {
  const inputRef = useObjectRef<HTMLInputElement>(fRef)

  return (
    <div className={cx(Styles.promoCodeInput, { [Styles.invalid]: props.isInvalid })}>
      <label htmlFor="promotion-code">Promotion code</label>
      {props.isApplied && props.appliedCodeName ? (
        <div className={Styles.appliedPillContainer}>
          <div className={Styles.promoAppliedPill}>
            <span>{props.appliedCodeName}</span>
            <button
              type="button"
              onClick={() => {
                if (inputRef.current) inputRef.current.value = ''
                props.onClear?.()
              }}
            >
              <X02 />
            </button>
          </div>
        </div>
      ) : (
        <div className={Styles.promoCodeInputRow}>
          <Input
            name="promotion-code"
            data-testid="promotion-code-input"
            id="promotionCode"
            placeholder="Promotion Code"
            onChange={props.onApply ? undefined : props.onBlur}
            onBlur={props.onApply ? undefined : props.onBlur}
            onKeyDown={(e) => {
              if (!props.onApply) return

              if (e.key === 'Enter') {
                e.preventDefault()
                props.onApply?.()
              }
            }}
            ref={inputRef}
            aria-disabled={props.isDisabled}
            disabled={props.isDisabled}
            size="xl"
          />
          {props.onApply && (
            <Button
              size="xl"
              onPress={props.onApply}
              isDisabled={props.isDisabled || props.isApplying}
              data-testid="apply-promo-code-button"
            >
              Apply
            </Button>
          )}
        </div>
      )}

      {props.isApplied && props.appliedDescription && (
        <Text variant="metadata" color="var(--dark-60)">
          {props.appliedDescription}
        </Text>
      )}

      <div className={Styles.error}>Not a valid promotion code</div>
    </div>
  )
})

export const FreeTrialCardForm = ({
  isProcessing,
  isPaywall,
  setIsProcessing,
  redirectTo,
  onboardingDocPath,
  registrationType,
  promotionCode,
  setAppliedPromoCode,
  readyOverride,
  paymentPlans,
  infoPanel,
  newlyCreatedWorkspaceId,
}: Props) => {
  const stripe = useStripe()
  const elements = useElements()
  const isCI =
    localStorage.getItem('blaze-ci') && process.env.REACT_APP_ENV && ['ci', 'dev'].includes(process.env.REACT_APP_ENV)
  const [ready, setReady] = useState(readyOverride === undefined ? isCI : readyOverride)
  const [isCardHolderEmpty, setIsCardHolderEmpty] = useState(false)
  const [promoCodeInvalid, setPromoCodeInvalid] = useState(false)
  const [isCardPayment, setIsCardPayment] = useState(true)
  const currentWorkspaceId = useCurrentWorkspaceId()

  const [interval, setPlanInterval] = useState<PlanInterval>('month')
  const { isMobile } = useResponsiveDesign()
  const cardHolderNameRef = useRef<HTMLInputElement>(null)
  const promoCodeRef = useRef<HTMLInputElement>(null)
  const proPlan = paymentPlans.find((plan) => plan.key === PaymentPlanKey.BlazePro)
  const creatorPlan = paymentPlans.find((plan) => plan.key === PaymentPlanKey.BlazeCreator)
  const queryParams = useQueryParams()

  const fetchPromotionCode = async () => {
    if (promoCodeRef.current?.value) {
      const res = await StripePromotionCodeClient.get(promoCodeRef.current.value)
      if (res.type === SUCCESS_RESPONSE_TYPE && res.data.promotion_code?.coupon?.id) {
        setAppliedPromoCode(res.data.promotion_code)
        setPromoCodeInvalid(false)
      } else {
        setPromoCodeInvalid(true)
        setAppliedPromoCode()
      }
    } else {
      setAppliedPromoCode()
      setPromoCodeInvalid(false)
    }
  }

  const successUrl = useMemo<string>(() => {
    const searchParams = new URLSearchParams({
      redirectTo: redirectTo || onboardingDocPath || '',
      registrationType: registrationType || '',
      isPaywall: isPaywall.toString(),
      promotionCode: promotionCode || '',
      interval,
      workspaceId: newlyCreatedWorkspaceId || currentWorkspaceId,
      ...queryParams,
    }).toString()
    return `${REACT_APP_FRONTEND_URL}${PAYMENT_STATUS_PATH}?${searchParams}`
  }, [
    isPaywall,
    onboardingDocPath,
    redirectTo,
    registrationType,
    promotionCode,
    interval,
    queryParams,
    currentWorkspaceId,
    newlyCreatedWorkspaceId,
  ])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (isCardPayment && cardHolderNameRef.current?.value === '') {
      setIsCardHolderEmpty(true)
      return
    }

    trackEvent(TRIAL_REQUESTED)

    if (isCI) return window.open(successUrl, '_self')

    if (!stripe || !elements) return

    setIsProcessing(true)

    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: successUrl,
        payment_method_data: {
          billing_details: {
            name: cardHolderNameRef.current?.value,
          },
        },
      },
    })

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)

      showToastMessage(`${error.message} Try another payment method.`, 'error')
      reportError('Error: stripe.confirmPayment failed', { metaData: error })
      trackEventInHeap(HeapEventType.Payment, { action: 'payment-failed-early' })
    } else {
      // Customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }

    setIsProcessing(false)
  }

  return (
    <form onSubmit={handleSubmit} className={Styles.form}>
      {proPlan && creatorPlan && (
        <div className={Styles.monthlyAnnualToggle}>
          <MonthlyAnnualToggle
            paymentPlan={proPlan}
            interval={interval}
            setPlanInterval={setPlanInterval}
            annualDiscountPercent={ANNUAL_DISCOUNT_PERCENT}
          />
          {!isMobile && (
            <p>
              You can downgrade to {creatorPlan.name} Plan ${creatorPlan.price.month.unit_amount}/month at any time
            </p>
          )}
        </div>
      )}

      {infoPanel}

      <div className={Styles.cardHolderNameAndPromoCodeContainer}>
        <CardholderNameInput
          ref={cardHolderNameRef}
          isInvalid={isCardHolderEmpty && isCardPayment}
          isDisabled={!ready}
          setIsInvalid={setIsCardHolderEmpty}
        />
        <PromotionCodeInput
          ref={promoCodeRef}
          onBlur={fetchPromotionCode}
          isDisabled={!ready}
          isInvalid={promoCodeInvalid}
        />
      </div>
      <PaymentElement
        onReady={() => setReady(true)}
        className={Styles.paymentElement}
        onChange={(e) => {
          const selectedPaymentMethod = e?.value?.type
          setIsCardPayment(selectedPaymentMethod === 'card')
        }}
        options={{
          paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'cashapp'],
          fields: {
            billingDetails: {
              name: 'never',
            },
          },
        }}
      />
      {ready && (
        <>
          <p className={Styles.authorizationMsg}>
            To authorize your card, a small amount will be held and immediately released. By continuing, you agree to
            Blaze’s{' '}
            <a target="_blank" href="https://www.blaze.ai/terms" rel="noreferrer">
              Terms of Service
            </a>
            . Read our{' '}
            <a target="_blank" href="https://www.blaze.ai/privacy-policy" rel="noreferrer">
              Privacy Policy
            </a>
            .
          </p>

          {isMobile && <Testimonials currentPage="blazeStartFreeTrial" newFormat />}
        </>
      )}
      <div className={Styles.buttonContainer}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={isProcessing}
          fullWidth={isMobile}
          size="lg"
          data-testid="start-trial-button"
          id="start-trial-button"
          endIcon={!isProcessing ? ArrowRight : undefined}
        >
          <div className={Styles.buttonContent}>
            {isProcessing && <Spinner className={Styles.loadingSpinner} />}
            {isProcessing ? 'Here we go...' : 'Start your free trial'}
          </div>
        </Button>
      </div>
    </form>
  )
}
